import { useState } from 'react';
import { useMe } from './hooks/useMe';
import { usePlacements } from './hooks/usePlacements';
import { Header } from './components/Header';
import { AccessDenied } from './components/AccessDenied';
import { LoadingState } from './components/LoadingState';
import { Forecaster } from './components/Forecaster';
import { SettingsPanel } from './components/SettingsPanel';
import { ContractorMonitor } from './components/ContractorMonitor';
import type { User } from './lib/types';

export default function App() {
  const me = useMe();

  if (me.status === 'loading') return <LoadingState label="Loading…" />;
  if (me.status === 'signing-in') return <LoadingState label="Signing you in…" />;
  if (me.status === 'error') {
    return (
      <AccessDenied
        reason={`Something went wrong: ${me.message}. Please close this tab and try again.`}
      />
    );
  }
  if (me.status === 'forbidden') {
    return <AccessDenied reason={me.reason} principal={me.principal} />;
  }

  return <Body user={me.user} />;
}

type Tab = 'forecast' | 'monitor';

const TABS = [
  { key: 'forecast', label: 'WGP Forecast' },
  { key: 'monitor', label: 'Contractor Monitor' },
] as const;

/** `?tab=monitor` deep-links straight to the Contractor Monitor (used by the Friday email). */
function tabFromUrl(): Tab {
  return new URLSearchParams(window.location.search).get('tab') === 'monitor' ? 'monitor' : 'forecast';
}

function Body({ user }: { user: User }) {
  const [tab, setTabState] = useState<Tab>(tabFromUrl);
  // Only fetch the forecast data once the forecast tab has been opened.
  const [forecastVisited, setForecastVisited] = useState(tab === 'forecast');
  const placementsState = usePlacements(forecastVisited);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const setTab = (next: Tab) => {
    setTabState(next);
    if (next === 'forecast') setForecastVisited(true);
    const url = new URL(window.location.href);
    if (next === 'monitor') url.searchParams.set('tab', 'monitor');
    else url.searchParams.delete('tab');
    window.history.replaceState(null, '', url.toString());
  };

  return (
    <div className="min-h-screen">
      <Header
        user={user}
        tabs={TABS}
        activeTab={tab}
        onTabChange={setTab}
        onOpenSettings={user.role === 'leadership' ? () => setSettingsOpen(true) : undefined}
      />
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}

      {tab === 'monitor' && <ContractorMonitor user={user} />}

      {tab === 'forecast' && (
        <>
          {placementsState.status === 'loading' && <LoadingState label="Loading placements…" />}
          {placementsState.status === 'error' && (
            <div className="ds-page">
              <div className="ds-panel" style={{ maxWidth: 640, margin: '40px auto', boxShadow: 'inset 4px 0 0 var(--rag-red)' }}>
                <h3>Couldn&rsquo;t load placements</h3>
                <p style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>{placementsState.message}</p>
              </div>
            </div>
          )}
          {placementsState.status === 'ok' && (
            <Forecaster user={placementsState.user} placements={placementsState.placements} />
          )}
        </>
      )}
    </div>
  );
}